import React, { useState, useTransition } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Grid,
  Divider,
  Alert,
  Paper,
  CircularProgress,
  Tooltip,
  Autocomplete,
  Tabs,
  Tab,
  useTheme,
  alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
  BugReport as BugIcon,
  Task as TaskIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  AddTask as AddTaskIcon,
  Delete as DeleteIcon,
  AttachFile as AttachmentIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Image as ImageIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Visibility as ViewIcon,
  Visibility as VisibilityIcon,
  AutoAwesome as AIIcon,
  Comment as CommentIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { Issue, User, IssueType, IssueStatus, Priority, Attachment, Comment, BoardColumn } from '../../types';
import { apiService } from '../../services/api';

interface IssueDetailsModalProps {
  open: boolean;
  onClose: () => void;
  issue: Issue | null;
  onUpdate?: (updatedIssue: Issue) => void;
  onOpenIssue?: (issue: Issue) => void;
  boardColumns?: BoardColumn[]; // Add board columns prop
  availableUsers?: User[]; // Add available users prop (board members when called from Board page)
  parentIssue?: Issue | null; // Track parent issue for navigation
}

const IssueDetailsModal: React.FC<IssueDetailsModalProps> = ({
  open,
  onClose,
  issue,
  onUpdate,
  onOpenIssue,
  boardColumns = [], // Add board columns prop with default empty array
  availableUsers: providedUsers, // Add available users prop
  parentIssue: providedParentIssue // Parent issue to reopen when this modal closes
}) => {
  const [parentIssue, setParentIssue] = useState<Issue | null>(providedParentIssue || null);
  const [openSubtaskId, setOpenSubtaskId] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isPending, startEditingTransition] = useTransition();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [availableStories, setAvailableStories] = useState<Issue[]>([]);
  const [storiesLoaded, setStoriesLoaded] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [assigneeSelectOpen, setAssigneeSelectOpen] = useState(false);
  const [hydratedIssue, setHydratedIssue] = useState<Issue | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [activity, setActivity] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [mainTab, setMainTab] = useState(0); // Main tab: 0 = Details, 1 = Sub Tasks (Story only), 2 = Bugs
  const [relatedSubtasks, setRelatedSubtasks] = useState<Issue[]>([]);
  const [relatedBugs, setRelatedBugs] = useState<Issue[]>([]);
  const [loadingRelatedIssues, setLoadingRelatedIssues] = useState(false);
  const [subtaskBugsMap, setSubtaskBugsMap] = useState<Record<string, Issue[]>>({});

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: IssueType.STORY,
    priority: Priority.MEDIUM,
    status: 'todo' as string,
    columnId: '' as string, // Add columnId for custom columns
    storyPoints: 0,
    originalEstimate: 0,
    remainingEstimate: 0,
    assigneeIds: [] as string[],
    dueDate: '',
    acceptanceCriteria: '',
    labels: [] as string[],
    parentId: '' as string
  });

  const [subtasks, setSubtasks] = useState<Array<{
    id?: string;
    title: string;
    description: string;
    assigneeId: string;
    storyPoints: number;
    priority: Priority;
    status: string;
  }>>([]);

  React.useEffect(() => {
    const base = hydratedIssue || issue;
    if (base) {
      // Set parent issue from provided prop or from issue's parent property
      const currentParent = providedParentIssue || base.parent;
      if (currentParent && !parentIssue) {
        setParentIssue(currentParent);
      }
      
      setFormData({
        title: base.title || '',
        description: base.description || '',
        type: base.type,
        priority: base.priority,
        status: base.status,
        columnId: base.customFields?.columnId || '',
        storyPoints: base.storyPoints || 0,
        originalEstimate: base.originalEstimate || 0,
        remainingEstimate: base.remainingEstimate || 0,
        assigneeIds: [
          ...(base.assigneeId ? [base.assigneeId] : []),
          ...(base.assignments?.map(a => a.user?.id).filter((id): id is string => !!id) || [])
        ],
        dueDate: base.dueDate ? new Date(base.dueDate).toISOString().split('T')[0] : '',
        acceptanceCriteria: base.acceptanceCriteria || '',
        labels: base.labels || [],
        parentId: base.parentId || ''
      });
      // Initialize subtasks - extract assigneeId from multiple sources
      setSubtasks((base.subtasks || []).map(st => {
        // Try to get assigneeId from: direct assigneeId, assignments array (first assignment's userId or user.id), or assignee relation
        let assigneeId = st.assigneeId || '';
        if (!assigneeId && st.assignments && st.assignments.length > 0) {
          const firstAssignment = st.assignments[0];
          assigneeId = firstAssignment.userId || firstAssignment.user?.id || '';
        }
        if (!assigneeId && (st as any).assignee?.id) {
          assigneeId = (st as any).assignee.id;
        }
        return {
          id: st.id,
          title: st.title,
          description: st.description || '',
          assigneeId: assigneeId,
          storyPoints: st.storyPoints || 0,
          priority: st.priority,
          status: st.status
        };
      }) || []);
      const t = setTimeout(() => {
        loadAttachments();
      }, 0);
      return () => clearTimeout(t);
    }
  }, [issue, hydratedIssue]);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (open && issue) {
        try {
          const full = await apiService.getIssue(issue.id);
          if (!cancelled) {
            setHydratedIssue(full);
            // Update parent issue if available
            const currentParent = providedParentIssue || full.parent;
            if (currentParent) {
              setParentIssue(currentParent);
            }
          }
          setLoadingComments(true);
          const [list, act] = await Promise.all([
            apiService.getIssueComments(issue.id),
            apiService.getIssueActivity(issue.id)
          ]);
          if (!cancelled) {
            setComments(list);
            setActivity(act);
          }
          setLoadingComments(false);

          // Load related issues based on issue type
          if (full.type === IssueType.STORY || full.type === IssueType.SUBTASK || full.type === IssueType.TASK) {
            setLoadingRelatedIssues(true);
            try {
              const issueFilters: any = {
                projectId: full.projectId,
                page: 1,
                limit: 100
              };

              if (full.type === IssueType.STORY) {
                // For Story: fetch subtasks and bugs (including bugs from subtasks)
                const [subtasksData, bugsData] = await Promise.all([
                  apiService.getIssues({ ...issueFilters, type: [IssueType.SUBTASK], parentId: full.id }),
                  apiService.getIssues({ ...issueFilters, type: [IssueType.BUG], parentId: full.id })
                ]);
                if (!cancelled) {
                  // PaginatedResponse<Issue[]> means data is Issue[][], so we need to flatten
                  const subtasksRaw = subtasksData.data as any;
                  const bugsRaw = bugsData.data as any;
                  const subtasks: Issue[] = Array.isArray(subtasksRaw) 
                    ? (Array.isArray(subtasksRaw[0]) ? subtasksRaw.flat() : subtasksRaw)
                    : [];
                  let bugs: Issue[] = Array.isArray(bugsRaw) 
                    ? (Array.isArray(bugsRaw[0]) ? bugsRaw.flat() : bugsRaw)
                    : [];
                  
                  // Also fetch bugs from all subtasks
                  if (subtasks.length > 0) {
                    const subtaskBugsPromises = subtasks.map(subtask => 
                      apiService.getIssues({ 
                        ...issueFilters, 
                        type: [IssueType.BUG], 
                        parentId: subtask.id 
                      })
                    );
                    const subtaskBugsResults = await Promise.all(subtaskBugsPromises);
                    subtaskBugsResults.forEach(result => {
                      if (!cancelled) {
                        const subtaskBugsRaw = result.data as any;
                        const subtaskBugs: Issue[] = Array.isArray(subtaskBugsRaw) 
                          ? (Array.isArray(subtaskBugsRaw[0]) ? subtaskBugsRaw.flat() : subtaskBugsRaw)
                          : [];
                        bugs = [...bugs, ...subtaskBugs];
                      }
                    });
                  }
                  
                  // Remove duplicate bugs (in case of any edge cases)
                  const uniqueBugs = bugs.filter((bug, index, self) => 
                    index === self.findIndex(b => b.id === bug.id)
                  );
                  
                  console.log('🔍 Loaded related issues - Subtasks:', subtasks.length, 'Bugs (including from subtasks):', uniqueBugs.length);
                  console.log('🔍 Bugs data:', uniqueBugs);
                  setRelatedSubtasks(subtasks);
                  setRelatedBugs(uniqueBugs);
                  
                  // Load bugs for each subtask
                  if (subtasks.length > 0) {
                    const bugsMap: Record<string, Issue[]> = {};
                    const bugsPromises = subtasks.map(async (subtask) => {
                      try {
                        const bugsData = await apiService.getIssues({ 
                          projectId: full.projectId, 
                          type: [IssueType.BUG], 
                          parentId: subtask.id,
                          page: 1,
                          limit: 100
                        });
                        const bugsRaw = bugsData.data as any;
                        const bugs: Issue[] = Array.isArray(bugsRaw) 
                          ? (Array.isArray(bugsRaw[0]) ? bugsRaw.flat() : bugsRaw)
                          : [];
                        return { subtaskId: subtask.id, bugs };
                      } catch (error) {
                        console.error('Error loading bugs for subtask:', error);
                        return { subtaskId: subtask.id, bugs: [] };
                      }
                    });
                    
                    const bugsResults = await Promise.all(bugsPromises);
                    bugsResults.forEach(({ subtaskId, bugs }) => {
                      bugsMap[subtaskId] = bugs;
                    });
                    
                    if (!cancelled) {
                      setSubtaskBugsMap(bugsMap);
                    }
                  }
                }
              } else if (full.type === IssueType.SUBTASK || full.type === IssueType.TASK) {
                // For Subtask/Task: fetch only bugs
                const bugsData = await apiService.getIssues({ ...issueFilters, type: [IssueType.BUG], parentId: full.id });
                if (!cancelled) {
                  // PaginatedResponse<Issue[]> means data is Issue[][], so we need to flatten
                  const bugsRaw = bugsData.data as any;
                  const bugs: Issue[] = Array.isArray(bugsRaw) 
                    ? (Array.isArray(bugsRaw[0]) ? bugsRaw.flat() : bugsRaw)
                    : [];
                  setRelatedBugs(bugs);
                  setRelatedSubtasks([]);
                }
              }
            } catch (error) {
              console.error('Error loading related issues:', error);
            } finally {
              if (!cancelled) {
                setLoadingRelatedIssues(false);
              }
            }
          } else {
            setRelatedSubtasks([]);
            setRelatedBugs([]);
          }
        } catch {}
      } else {
        setHydratedIssue(null);
        setRelatedSubtasks([]);
        setRelatedBugs([]);
        setSubtaskBugsMap({});
      }
    };
    run();
    // Reset main tab to Details when issue changes
    setMainTab(0);
    return () => { cancelled = true; };
  }, [open, issue?.id]);

  React.useEffect(() => {
    if (open && issue && !usersLoaded) {
      loadAvailableUsers();
    }
  }, [open, issue, usersLoaded]);

  React.useEffect(() => {
    if (open && issue && isEditing && !storiesLoaded && (issue.type === IssueType.BUG || issue.type === IssueType.SUBTASK)) {
      loadAvailableStories();
    }
  }, [open, issue, isEditing, storiesLoaded]);

  const loadAvailableUsers = async () => {
    try {
      // Use provided users if available (board members), otherwise fetch project assignments
      if (providedUsers && providedUsers.length > 0) {
        console.log('🔍 IssueDetailsModal: Using provided users (board members):', providedUsers);
        setAvailableUsers(providedUsers);
      } else {
        const teamMembers = await apiService.getProjectAssignments(issue?.projectId || '');
        const projectUsers = teamMembers.map((a: any) => a.user).filter(Boolean);
        console.log('🔍 IssueDetailsModal: Using project assignments (all project members):', projectUsers);
        setAvailableUsers(projectUsers);
      }
      setUsersLoaded(true);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadAvailableStories = async () => {
    try {
      // Load stories, tasks, and subtasks for parent selection (bugs can be linked to any of these)
      const parentIssuesData = await apiService.getIssues({ 
        projectId: issue?.projectId, 
        type: [IssueType.STORY, IssueType.TASK, IssueType.SUBTASK] 
      });
      const parentIssuesArray = (parentIssuesData.data as unknown as Issue[]) || [];
      // Filter out the current issue and its subtasks to avoid circular references
      const filteredIssues = parentIssuesArray.filter(parentIssue => parentIssue.id !== issue?.id);
      setAvailableStories(filteredIssues);
      setStoriesLoaded(true);
    } catch (error) {
      console.error('Error loading parent issues:', error);
    }
  };

  const loadAttachments = async () => {
    if (!issue) return;
    try {
      setAttachmentsLoading(true);
      const attachments = await apiService.getIssueAttachments(issue.id);
      setAttachments(attachments);
    } catch (error) {
      console.warn('Failed to load attachments:', error);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !issue) return;
    try {
      setUploadingAttachment(true);
      setError(null);
      const attachment = await apiService.uploadAttachment(issue.id, file);
      setAttachments(prev => [attachment, ...prev]);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    try {
      await apiService.deleteAttachment(attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete attachment');
    }
  };

  const getAttachmentUrl = (attachment: Attachment): string => {
    // Always use the file serving endpoint for both local and S3 files
    // This ensures proper authentication and handling
    const baseUrl = process.env.REACT_APP_API_URL === '/api/v1' 
      ? 'http://localhost:4000/api/v1' 
      : (process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1');
    
    return `${baseUrl}/attachments/file/${attachment.id}`;
  };

  const getAttachmentIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <ImageIcon />;
    if (mimeType === 'application/pdf') return <PictureAsPdfIcon />;
    if (mimeType.startsWith('text/')) return <DescriptionIcon />;
    return <InsertDriveFileIcon />;
  };

  const handleViewAttachment = (attachment: Attachment) => {
    const url = getAttachmentUrl(attachment);
    const token = localStorage.getItem('authToken');
    const urlWithToken = token ? `${url}?token=${encodeURIComponent(token)}` : url;
    window.open(urlWithToken, '_blank');
  };

  const handleDownloadAttachment = (attachment: Attachment) => {
    const url = getAttachmentUrl(attachment);
    const token = localStorage.getItem('authToken');
    const urlWithToken = token ? `${url}?token=${encodeURIComponent(token)}` : url;
    const link = document.createElement('a');
    link.href = urlWithToken;
    link.download = attachment.originalName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const addSubtask = () => {
    setSubtasks(prev => [...prev, {
      title: '',
      description: '',
      assigneeId: '',
      storyPoints: 0,
      priority: Priority.MEDIUM,
              status: 'todo'
    }]);
  };

  const removeSubtask = (index: number) => {
    setSubtasks(prev => prev.filter((_, i) => i !== index));
  };

  const updateSubtask = (index: number, field: string, value: any) => {
    setSubtasks(prev => prev.map((st, i) => 
      i === index ? { ...st, [field]: value } : st
    ));
  };

  const handleSave = async () => {
    if (!issue) return;
    setLoading(true);
    setError(null);
    try {
      // Prepare customFields with columnId if it's a custom column
      const customFields = issue.customFields || {};
      if (formData.columnId) {
        customFields.columnId = formData.columnId;
      }

      // Validate that due date is required
      if (!formData.dueDate || formData.dueDate.trim() === '') {
        setError('Due date is required for all issues. Please select a due date.');
        setLoading(false);
        return;
      }

      const updatedIssue = await apiService.updateIssue(issue.id, {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        priority: formData.priority,
        status: formData.status,
        storyPoints: formData.storyPoints,
        originalEstimate: formData.originalEstimate,
        remainingEstimate: formData.remainingEstimate,
        dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : undefined,
        acceptanceCriteria: formData.acceptanceCriteria,
        labels: formData.labels,
        parentId: formData.parentId || undefined,
        assigneeId: formData.assigneeIds.length > 0 ? formData.assigneeIds[0] : undefined,
        customFields: formData.columnId ? customFields : issue.customFields
      });
      {
        // Handle secondary assignments (all assignees except the first one)
        const secondaryAssignees = formData.assigneeIds.slice(1);
        const current = new Set((issue.assignments || []).map(a => a.user?.id).filter(Boolean) as string[]);
        const desired = new Set(secondaryAssignees);
        const toAdd = Array.from(desired).filter(id => !current.has(id));
        const toRemove = Array.from(current).filter(id => !desired.has(id));
        
        if (toAdd.length > 0) {
          await apiService.assignUsersToIssue(issue.id, toAdd);
        }
        for (const userId of toRemove) {
          await apiService.removeUserFromIssue(issue.id, userId);
        }
      }
      for (const subtask of subtasks) {
        if (subtask.title.trim()) {
          if (subtask.id) {
            await apiService.updateIssue(subtask.id, {
              title: subtask.title,
              description: subtask.description,
              priority: subtask.priority,
              status: subtask.status,
              storyPoints: subtask.storyPoints,
              assigneeId: subtask.assigneeId || undefined
            });
            const existing = await apiService.getIssueAssignments(subtask.id);
            const currentIds = new Set(existing.map((a: any) => a.userId));
            const desiredIds = new Set(subtask.assigneeId ? [subtask.assigneeId] : []);
            const toAdd = Array.from(desiredIds).filter(id => !currentIds.has(id));
            const toRemove = Array.from(currentIds).filter(id => !desiredIds.has(id));
            if (toAdd.length > 0) {
              await apiService.assignUsersToIssue(subtask.id, toAdd);
            }
            for (const userId of toRemove) {
              await apiService.removeUserFromIssue(subtask.id, userId);
            }
          } else {
            await apiService.createIssue({
              title: subtask.title,
              description: subtask.description,
              type: IssueType.SUBTASK,
              priority: subtask.priority,
              projectId: issue.projectId,
              assigneeId: subtask.assigneeId || undefined,
              storyPoints: subtask.storyPoints,
              parentId: issue.id
            });
          }
        }
      }
      console.log('🔄 IssueDetailsModal: Calling onUpdate with:', updatedIssue);
      onUpdate?.(updatedIssue);
      setIsEditing(false);
      onClose();
      return;
    } catch (error) {
      setError('Failed to update issue');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Prevent infinite loop - only auto-reopen if we're actually closing and have a parent
    // and we haven't already triggered a close
    if (isClosing) {
      onClose();
      return;
    }
    
    setIsEditing(false);
    setError(null);
    
    // Only auto-reopen parent if:
    // 1. We have a parent issue
    // 2. We have onOpenIssue callback
    // 3. The current issue is NOT the parent itself (prevent loop)
    // 4. The providedParentIssue is set (meaning we were opened from a parent)
    if (providedParentIssue && parentIssue && onOpenIssue && issue?.id !== parentIssue.id) {
      setIsClosing(true);
      // Use setTimeout to ensure this modal closes first
      setTimeout(() => {
        onOpenIssue(parentIssue);
        setIsClosing(false);
      }, 100);
    }
    
    onClose();
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !issue) return;
    
    setPostingComment(true);
    try {
      await apiService.addComment(issue.id, newComment);
      setNewComment('');
      // Reload comments
      const commentsData = await apiService.getIssueComments(issue.id);
      setComments(commentsData || []);
    } catch (error) {
      console.error('Error adding comment:', error);
      setError('Failed to add comment');
    } finally {
      setPostingComment(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!issue) return;
    
    setGeneratingAI(true);
    try {
      // Simulate AI generation - in real implementation, this would call an AI service
      const aiGeneratedContent = `AI-generated acceptance criteria for "${formData.title}":
      
1. The feature should be fully functional and meet the specified requirements
2. All user interactions should work as expected
3. The feature should be responsive and work on all supported devices
4. Error handling should be implemented for edge cases
5. The feature should be accessible and follow WCAG guidelines
6. Performance should meet the defined benchmarks
7. The feature should be tested and verified by QA team
8. Documentation should be updated to reflect the new functionality`;

      setFormData(prev => ({ ...prev, acceptanceCriteria: aiGeneratedContent }));
    } catch (error) {
      console.error('Error generating AI content:', error);
      setError('Failed to generate AI content');
    } finally {
      setGeneratingAI(false);
    }
  };

  const getIssueTypeIcon = (type: IssueType) => {
    switch (type) {
      case IssueType.STORY: return <AssignmentIcon />;
      case IssueType.BUG: return <BugIcon />;
      case IssueType.TASK: return <TaskIcon />;
      default: return <AssignmentIcon />;
    }
  };

  const getIssueTypeColor = (type: IssueType) => {
    switch (type) {
      case IssueType.STORY: return 'primary';
      case IssueType.BUG: return 'error';
      case IssueType.TASK: return 'info';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.HIGH: return 'error';
      case Priority.MEDIUM: return 'warning';
      case Priority.LOW: return 'success';
      default: return 'default';
    }
  };

  if (!issue) return null;

  const handleDialogContentClick = () => {
    if (!isEditing) startEditingTransition(() => setIsEditing(true));
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      TransitionProps={{ timeout: 0 }}
      disableAutoFocus
      disableRestoreFocus
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
          minHeight: '80vh'
        }
      }}
    >
       {/* Modern Header */}
       <DialogTitle sx={{ 
         pb: 0, 
         background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
         color: 'white',
         position: 'relative',
         overflow: 'hidden'
       }}>
         <Box sx={{ 
           position: 'absolute', 
           top: 0, 
           right: 0, 
           width: '200px', 
           height: '200px', 
           background: 'rgba(255,255,255,0.1)', 
           borderRadius: '50%', 
           transform: 'translate(50%, -50%)' 
         }} />
         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              icon={getIssueTypeIcon(issue.type)}
              label={issue.type}
               sx={{ 
                 bgcolor: 'rgba(255,255,255,0.2)', 
                 color: 'white',
                 backdropFilter: 'blur(10px)',
                 border: '1px solid rgba(255,255,255,0.3)'
               }}
              size="small"
            />
            <Box>
               <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.5, color: 'white' }}>
                {issue.issueKey || `#${issue.issueNumber}`}
              </Typography>
               <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {hydratedIssue?.project?.name || issue.project?.name}
              </Typography>
            </Box>
          </Box>
           
           {/* Assigned Users Avatars */}
           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
             {issue.assignments && issue.assignments.length > 0 && (
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 2 }}>
                 <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', mr: 1 }}>
                   Assigned:
                 </Typography>
                 <Box sx={{ display: 'flex', gap: -0.5 }}>
                   {issue.assignments.slice(0, 3).map((assignment, index) => (
                     <Tooltip key={assignment.id} title={`${assignment.user?.firstName} ${assignment.user?.lastName}`} arrow>
                       <Avatar
                         sx={{ 
                           width: 28, 
                           height: 28, 
                           fontSize: '0.75rem',
                           border: '2px solid rgba(255,255,255,0.3)',
                           ml: index > 0 ? -0.5 : 0,
                           zIndex: issue.assignments!.length - index,
                           bgcolor: 'rgba(255,255,255,0.2)',
                           color: 'white',
                           '&:hover': {
                             zIndex: 10,
                             transform: 'scale(1.1)',
                             transition: 'all 0.2s'
                           }
                         }}
                       >
                         {assignment.user?.firstName?.[0]}{assignment.user?.lastName?.[0]}
                       </Avatar>
                     </Tooltip>
                   ))}
                   {issue.assignments.length > 3 && (
                     <Tooltip title={`+${issue.assignments.length - 3} more users assigned`} arrow>
                       <Avatar
                         sx={{ 
                           width: 28, 
                           height: 28, 
                           fontSize: '0.75rem',
                           border: '2px solid rgba(255,255,255,0.3)',
                           ml: -0.5,
                           bgcolor: 'rgba(255,255,255,0.2)',
                           color: 'white'
                         }}
                       >
                         +{issue.assignments.length - 3}
                       </Avatar>
                     </Tooltip>
                   )}
                 </Box>
               </Box>
             )}
             
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isEditing ? (
                 <IconButton 
                   onClick={() => startEditingTransition(() => setIsEditing(true))} 
                   size="small" 
                   sx={{ 
                     bgcolor: 'rgba(255,255,255,0.2)', 
                     color: 'white',
                     backdropFilter: 'blur(10px)',
                     border: '1px solid rgba(255,255,255,0.3)',
                     '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                   }}
                 >
                <EditIcon />
              </IconButton>
            ) : (
                 <IconButton 
                   onClick={() => startEditingTransition(() => setIsEditing(false))} 
                   size="small" 
                   sx={{ 
                     bgcolor: 'rgba(255,255,255,0.2)', 
                     color: 'white',
                     backdropFilter: 'blur(10px)',
                     border: '1px solid rgba(255,255,255,0.3)',
                     '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                   }}
                 >
                <CancelIcon />
              </IconButton>
            )}
               <IconButton 
                 onClick={handleClose} 
                 size="small"
                 sx={{ 
                   bgcolor: 'rgba(255,255,255,0.2)', 
                   color: 'white',
                   backdropFilter: 'blur(10px)',
                   border: '1px solid rgba(255,255,255,0.3)',
                   '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                 }}
               >
              <CloseIcon />
            </IconButton>
             </Box>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent 
        onClick={() => !isEditing && startEditingTransition(() => setIsEditing(true))}
        sx={{ 
          p: 0, 
          bgcolor: '#f8fafc',
          minHeight: '70vh',
          maxHeight: '80vh',
          overflow: 'auto',
          cursor: isEditing ? 'default' : 'text',
          userSelect: isEditing ? 'auto' : 'none'
        }}
      >
        {error && (
          <Alert severity="error" sx={{ m: 3, mb: 0 }}>
            {error}
          </Alert>
        )}

        {/* Main Tabs - Only show for Story, Subtask, and Task */}
        {issue && (issue.type === IssueType.STORY || issue.type === IssueType.SUBTASK || issue.type === IssueType.TASK) && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white', px: 3 }}>
            <Tabs 
              value={mainTab} 
              onChange={(_, newValue) => setMainTab(newValue)}
              sx={{ minHeight: 48 }}
            >
              <Tab 
                label={
                  issue.type === IssueType.STORY ? 'Story Details' :
                  issue.type === IssueType.SUBTASK ? 'Subtask Details' :
                  'Task Details'
                }
                sx={{ textTransform: 'none', minHeight: 48 }}
              />
              {issue.type === IssueType.STORY && (
                <Tab 
                  icon={<TaskIcon />} 
                  label={`Sub Tasks (${relatedSubtasks.length})`} 
                  iconPosition="start"
                  sx={{ textTransform: 'none', minHeight: 48 }}
                />
              )}
              {(issue.type === IssueType.STORY || issue.type === IssueType.SUBTASK || issue.type === IssueType.TASK) && (
                <Tab 
                  icon={<BugIcon />} 
                  label={`Bugs (${relatedBugs.length})`} 
                  iconPosition="start"
                  sx={{ textTransform: 'none', minHeight: 48 }}
                />
              )}
            </Tabs>
          </Box>
        )}

        {/* Tab Content */}
        {issue && (
          <>
            {/* Details Tab (Tab 0) - Show for all issue types */}
            {mainTab === 0 && (
              <Box>
                {/* Modern Layout with Sidebar */}
                <Box sx={{ display: 'flex', minHeight: '100%' }}>
          {/* Main Content Area */}
          <Box sx={{ flex: 1, p: 3, overflow: 'auto', maxHeight: '70vh' }}>
            {/* Story Title Section */}
            <Paper sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
                  <TextField
                    fullWidth
                label="Story Title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    onFocus={() => !isEditing && startEditingTransition(() => setIsEditing(true))}
                InputProps={{ 
                  readOnly: !isEditing,
                  sx: { 
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    '& .MuiInputBase-input': { py: 1 }
                  }
                }}
                    multiline
                    rows={2}
                variant="outlined"
              />
            </Paper>

            {/* Description Section */}
            <Paper sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
                Description
              </Typography>
                  <TextField
                    fullWidth
                label="Describe the story..."
                    multiline
                rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    onFocus={() => !isEditing && startEditingTransition(() => setIsEditing(true))}
                    InputProps={{ readOnly: !isEditing }}
                variant="outlined"
              />
            </Paper>

             {/* Acceptance Criteria Section */}
             <Paper sx={{ 
               p: 3, 
               mb: 3, 
               borderRadius: 3,
               border: '1px solid #e2e8f0',
               boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
             }}>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                Acceptance Criteria
              </Typography>
                 {isEditing && (
                   <Button
                     startIcon={generatingAI ? <CircularProgress size={16} /> : <AIIcon />}
                     onClick={handleGenerateAI}
                     disabled={generatingAI}
                     variant="outlined"
                     size="small"
                     sx={{ textTransform: 'none' }}
                   >
                     {generatingAI ? 'Generating...' : 'Generate via AI'}
                   </Button>
                 )}
               </Box>
              <TextField
                fullWidth
                 label="Define what 'done' looks like..."
                multiline
                rows={4}
                value={formData.acceptanceCriteria}
                onChange={(e) => setFormData(prev => ({ ...prev, acceptanceCriteria: e.target.value }))}
                onFocus={() => !isEditing && startEditingTransition(() => setIsEditing(true))}
                InputProps={{ readOnly: !isEditing }}
                 variant="outlined"
               />
             </Paper>

            {/* Parent Issue Selection for Bugs/Subtasks */}
            {(formData.type === IssueType.BUG || formData.type === IssueType.SUBTASK) && (
              <Paper sx={{ 
                p: 3, 
                mb: 3, 
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
                  Parent Issue
              </Typography>
                <Autocomplete
                  options={availableStories}
                  getOptionLabel={(option) => `${option.issueKey || option.id} - ${option.title}`}
                  value={availableStories.find(s => s.id === formData.parentId) || null}
                  onChange={(_, newValue) => setFormData(prev => ({ ...prev, parentId: newValue?.id || '' }))}
                  disabled={!isEditing}
                  renderInput={(params) => (
              <TextField
                      {...params}
                      label="Select parent issue"
                      variant="outlined"
                      helperText="Choose the issue this bug/subtask belongs to (Story, Task, or Subtask)"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Box sx={{ fontWeight: 'bold' }}>
                          {option.issueKey || option.id} 
                          <Chip 
                            label={option.type} 
                            size="small" 
                            sx={{ ml: 1, textTransform: 'capitalize' }}
                            color={option.type === IssueType.STORY ? 'primary' : 
                                   option.type === IssueType.TASK ? 'secondary' : 'default'}
                          />
                        </Box>
                        <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>{option.title}</Box>
                      </Box>
                    </Box>
                  )}
                  filterOptions={(options, { inputValue }) => {
                    const searchTerm = inputValue.toLowerCase().trim();
                    if (!searchTerm) return options; // Return all options if no search term
                    
                    return options.filter(option => {
                      // Check issueKey (computed property)
                      const issueKey = option.issueKey || `${option.project?.storyPrefix || 'ST'}-${option.issueNumber}`;
                      const issueKeyMatch = issueKey.toLowerCase().includes(searchTerm);
                      
                      // Check title
                      const titleMatch = option.title?.toLowerCase().includes(searchTerm) || false;
                      
                      // Check ID
                      const idMatch = option.id?.toLowerCase().includes(searchTerm) || false;
                      
                      // Check type
                      const typeMatch = option.type?.toLowerCase().includes(searchTerm) || false;
                      
                      // Check description
                      const descriptionMatch = option.description?.toLowerCase().includes(searchTerm) || false;
                      
                      return issueKeyMatch || titleMatch || idMatch || typeMatch || descriptionMatch;
                    });
                  }}
                  loading={!storiesLoaded}
                  noOptionsText="No issues found"
                />
              </Paper>
            )}

            {/* Subtasks Section */}
            {formData.type === IssueType.STORY && (
              <Paper sx={{ 
                p: 3, 
                mb: 3, 
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                bgcolor: '#fafbfc'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  mb: 3,
                  pb: 2,
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddTaskIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      Subtasks
                    </Typography>
                    {subtasks.length > 0 && (
                      <Chip 
                        label={`${subtasks.length} subtask${subtasks.length !== 1 ? 's' : ''}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                  <Button
                    startIcon={<AddTaskIcon />}
                    onClick={() => setSubtasks(prev => [...prev, {
                      id: '',
                      title: '',
                      description: '',
                      assigneeId: '',
                      storyPoints: 0,
                      priority: Priority.MEDIUM,
                      status: IssueStatus.TODO
                    }])}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      textTransform: 'none',
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontWeight: 500,
                      borderColor: '#64748b',
                      color: '#334155',
                      '&:hover': {
                        borderColor: '#334155',
                        backgroundColor: 'rgba(51, 65, 85, 0.04)',
                      }
                    }}
                  >
                    Add Subtask
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {subtasks.map((subtask, index) => {
                    // Use hydratedIssue if available, otherwise fall back to issue
                    const baseIssue = hydratedIssue || issue;
                    const originalSubtask = baseIssue.subtasks?.find(st => st.id === subtask.id);
                    
                    // Try multiple sources for assignee in priority order:
                    // 1. State subtask.assigneeId (from hydrated issue)
                    // 2. originalSubtask.assigneeId
                    // 3. originalSubtask.assignments[0].userId or user.id
                    // 4. originalSubtask.assignee.id (from relation)
                    let assigneeId = subtask.assigneeId || '';
                    if (!assigneeId && originalSubtask) {
                      assigneeId = originalSubtask.assigneeId || '';
                      if (!assigneeId && originalSubtask.assignments && originalSubtask.assignments.length > 0) {
                        const firstAssignment = originalSubtask.assignments[0];
                        assigneeId = firstAssignment.userId || firstAssignment.user?.id || '';
                      }
                      if (!assigneeId && (originalSubtask as any).assignee?.id) {
                        assigneeId = (originalSubtask as any).assignee.id;
                      }
                    }
                    
                    const viewAssignee = assigneeId ? 
                      availableUsers.find(u => u.id === assigneeId) : null;
                    return (
                    <Paper
                      key={index}
                      sx={{ 
                        p: 3, 
                        border: '1px solid #e2e8f0', 
                        borderRadius: 3,
                        bgcolor: '#ffffff',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                          borderColor: '#cbd5e1'
                        }
                      }}
                      onClick={(e) => {
                        // Don't open if clicking on input fields or buttons
                        if (!isEditing && originalSubtask && originalSubtask.id && onOpenIssue) {
                          const target = e.target as HTMLElement;
                          if (!target.closest('input, button, select, textarea, .MuiAutocomplete-root, .MuiIconButton-root')) {
                            handleClose();
                            setOpenSubtaskId(originalSubtask.id);
                            // Fetch full subtask details before opening
                            apiService.getIssue(originalSubtask.id).then((fullSubtask) => {
                              // Set current issue as parent when opening subtask
                              onOpenIssue({ ...fullSubtask, parent: issue });
                            }).catch(() => {
                              // Fallback: open with available data
                              onOpenIssue({ ...originalSubtask, parent: issue });
                            });
                          }
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* First Row: Title and Assignee */}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Subtask Title"
                              value={subtask.title}
                              onChange={(e) => setSubtasks(prev => prev.map((st, i) => 
                                i === index ? { ...st, title: e.target.value } : st
                              ))}
                              variant="outlined"
                              placeholder="Enter subtask title..."
                            />
                          </Box>
                          <Box sx={{ minWidth: 200 }}>
                            {isEditing ? (
                              <Autocomplete
                                size="small"
                                options={[
                                  { id: '', firstName: 'Unassigned', lastName: '', email: '' },
                                  ...availableUsers
                                ]}
                                value={availableUsers.find(u => u.id === subtask.assigneeId) || { id: '', firstName: 'Unassigned', lastName: '', email: '' }}
                                onChange={(_, newValue) => {
                                  setSubtasks(prev => prev.map((st, i) => 
                                    i === index ? { ...st, assigneeId: newValue?.id || '' } : st
                                  ));
                                }}
                                getOptionLabel={(option) => {
                                  if (option.id === '') return 'Unassigned';
                                  return `${option.firstName} ${option.lastName}`;
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Assignee"
                                    placeholder="Search users..."
                                    size="small"
                                  />
                                )}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                      <Avatar sx={{ width: 20, height: 20, fontSize: '0.7rem', bgcolor: option.id === '' ? 'grey.300' : 'primary.main' }}>
                                        {option.id === '' ? (
                                          <PersonIcon sx={{ fontSize: 12 }} />
                                        ) : (
                                          `${option.firstName?.[0]}${option.lastName?.[0]}`
                                        )}
                                      </Avatar>
                                      <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            fontSize: '0.875rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontStyle: option.id === '' ? 'italic' : 'normal'
                                          }}
                                        >
                                          {option.id === '' ? 'Unassigned' : `${option.firstName} ${option.lastName}`}
                                        </Typography>
                                        {option.id !== '' && (
                                          <Typography 
                                            variant="caption" 
                                            color="text.secondary"
                                            sx={{ 
                                              fontSize: '0.75rem',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              display: 'block'
                                            }}
                                          >
                                            {option.email}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  </Box>
                                )}
                                filterOptions={(options, { inputValue }) => {
                                  if (!inputValue) return options;
                                  const searchLower = inputValue.toLowerCase();
                                  return options.filter(option => 
                                    option.id === '' || // Always show "Unassigned" option
                                    option.firstName.toLowerCase().includes(searchLower) ||
                                    option.lastName.toLowerCase().includes(searchLower) ||
                                    option.email.toLowerCase().includes(searchLower)
                                  );
                                }}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                              />
                            ) : (
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Assignee</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
                                  <Tooltip title={viewAssignee ? `${viewAssignee.firstName} ${viewAssignee.lastName}` : 'Unassigned'} arrow>
                                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                    {viewAssignee?.firstName?.[0]}{viewAssignee?.lastName?.[0]}
                                  </Avatar>
                                  </Tooltip>
                                  <Typography 
                                    variant="body2"
                                    sx={{ 
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      flex: 1
                                    }}
                                  >
                                    {viewAssignee ? `${viewAssignee.firstName} ${viewAssignee.lastName}` : 'Unassigned'}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Second Row: Story Points, Priority, and Status */}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                          <Box sx={{ minWidth: 120 }}>
                            <TextField
                              fullWidth
                              label="Story Points"
                              type="number"
                              size="small"
                              value={subtask.storyPoints}
                              onChange={(e) => setSubtasks(prev => prev.map((st, i) => 
                                i === index ? { ...st, storyPoints: parseInt(e.target.value) || 0 } : st
                              ))}
                              variant="outlined"
                              inputProps={{ min: 0, max: 100 }}
                            />
                          </Box>
                          <Box sx={{ minWidth: 140 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Priority</InputLabel>
                              <Select
                                value={subtask.priority}
                                onChange={(e) => setSubtasks(prev => prev.map((st, i) => 
                                  i === index ? { ...st, priority: e.target.value as Priority } : st
                                ))}
                                label="Priority"
                              >
                                <MenuItem value={Priority.LOW}>Low</MenuItem>
                                <MenuItem value={Priority.MEDIUM}>Medium</MenuItem>
                                <MenuItem value={Priority.HIGH}>High</MenuItem>
                                <MenuItem value={Priority.CRITICAL}>Critical</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box sx={{ minWidth: 140 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Status</InputLabel>
                              <Select
                                value={subtask.status}
                                onChange={(e) => setSubtasks(prev => prev.map((st, i) => 
                                  i === index ? { ...st, status: e.target.value as IssueStatus } : st
                                ))}
                                label="Status"
                              >
                                {boardColumns.map(column => (
                                  <MenuItem key={column.id} value={column.status}>
                                    {column.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                          <Box sx={{ flex: 1 }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* View Subtask Button - only show when not editing and subtask has an ID */}
                            {/* View Subtask Button - show for saved subtasks (with ID) */}
                            {!isEditing && subtask.id && onOpenIssue && (
                              <Tooltip title="View Subtask Details">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClose();
                                    setOpenSubtaskId(subtask.id!);
                                    // Fetch full subtask details before opening
                                    apiService.getIssue(subtask.id!).then((fullSubtask) => {
                                      // Set current issue as parent when opening subtask
                                      onOpenIssue({ ...fullSubtask, parent: baseIssue });
                                    }).catch(() => {
                                      // Fallback: open with available data from originalSubtask if available
                                      if (originalSubtask) {
                                        onOpenIssue({ ...originalSubtask, parent: baseIssue });
                                      }
                                    });
                                  }}
                                  sx={{ 
                                    color: 'primary.main',
                                    bgcolor: 'rgba(25, 118, 210, 0.1)',
                                    '&:hover': {
                                      bgcolor: 'rgba(25, 118, 210, 0.2)',
                                      color: 'primary.dark'
                                    }
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {isEditing && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSubtasks(prev => prev.filter((_, i) => i !== index));
                                }}
                                sx={{ 
                                  color: 'error.main',
                                  '&:hover': {
                                    bgcolor: 'error.light',
                                    color: 'error.dark'
                                  }
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        </Box>

                        {/* Third Row: Description */}
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            label="Description"
                            multiline
                            rows={2}
                            value={subtask.description}
                            onChange={(e) => setSubtasks(prev => prev.map((st, i) => 
                              i === index ? { ...st, description: e.target.value } : st
                            ))}
                            variant="outlined"
                            placeholder="Enter subtask description..."
                          />
                        </Box>
                      </Box>
                    </Paper>
                    );
                  })}
                </Box>
              </Paper>
            )}

             {/* Comments and Activity Section with Tabs */}
             <Paper sx={{ 
               p: 3, 
               mb: 3, 
               borderRadius: 3,
               border: '1px solid #e2e8f0',
               boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
             }}>
               <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
                 Comments & Activity
              </Typography>
               
               {/* Tabs */}
               <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                 <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                   <Tab 
                     icon={<CommentIcon />} 
                     label="Comments" 
                     iconPosition="start"
                     sx={{ textTransform: 'none' }}
                   />
                   <Tab 
                     icon={<TimelineIcon />} 
                     label="Activity" 
                     iconPosition="start"
                     sx={{ textTransform: 'none' }}
                   />
                 </Tabs>
               </Box>

               {/* Tab Content */}
               {activeTab === 0 && (
                 <Box>
                   {/* Add Comment */}
              <Box sx={{ mb: 3 }}>
                     <TextField
                       fullWidth
                       multiline
                       rows={3}
                       placeholder="Add a comment..."
                       value={newComment}
                       onChange={(e) => setNewComment(e.target.value)}
                       variant="outlined"
                       sx={{ mb: 2 }}
                     />
                     <Button
                       variant="outlined"
                       onClick={handleAddComment}
                       disabled={!newComment.trim() || postingComment}
                       startIcon={postingComment ? <CircularProgress size={16} /> : <EditIcon />}
                       sx={{
                         textTransform: 'none',
                         borderColor: '#64748b',
                         color: '#334155',
                         '&:hover': {
                           borderColor: '#334155',
                           backgroundColor: 'rgba(51, 65, 85, 0.04)',
                         },
                         '&:disabled': {
                           borderColor: '#cbd5e1',
                           color: '#94a3b8',
                         }
                       }}
                     >
                       {postingComment ? 'Posting...' : 'Add Comment'}
                     </Button>
                   </Box>

                   {/* Comments List */}
                   <Box sx={{ maxHeight: 250, overflowY: 'auto', overflowX: 'hidden' }}>
                     {loadingComments ? (
                       <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                         <CircularProgress />
                       </Box>
                     ) : (
                       <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                         {comments.map((comment) => (
                           <Box key={comment.id} sx={{ 
                             p: 2, 
                             bgcolor: '#f8fafc', 
                             borderRadius: 2, 
                             border: '1px solid #e2e8f0' 
                           }}>
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                               <Tooltip title={`${comment.author?.firstName} ${comment.author?.lastName}`} arrow>
                                 <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                   {comment.author?.firstName?.[0]}{comment.author?.lastName?.[0]}
                            </Avatar>
                               </Tooltip>
                               <Typography variant="caption" fontWeight="medium">
                                 {comment.author?.firstName} {comment.author?.lastName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                 {new Date(comment.createdAt).toLocaleString()}
                              </Typography>
                            </Box>
                             <Typography variant="body2">{comment.content}</Typography>
                          </Box>
                         ))}
                         {comments.length === 0 && (
                           <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                             No comments yet. Be the first to comment!
                           </Typography>
                         )}
                       </Box>
                     )}
                   </Box>
                 </Box>
               )}

               {activeTab === 1 && (
                 <Box>
                   {/* Activity List */}
                   <Box sx={{ maxHeight: 250, overflowY: 'auto', overflowX: 'hidden' }}>
                     {loadingComments ? (
                       <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                         <CircularProgress />
                       </Box>
                     ) : (
                       <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                         {activity.map((activityItem) => (
                           <Box key={activityItem.id} sx={{ 
                             p: 2, 
                             bgcolor: '#f8fafc', 
                             borderRadius: 2, 
                             border: '1px solid #e2e8f0' 
                           }}>
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                               <Tooltip title={`${activityItem.author?.firstName} ${activityItem.author?.lastName}`} arrow>
                                 <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                   {activityItem.author?.firstName?.[0]}{activityItem.author?.lastName?.[0]}
                              </Avatar>
                               </Tooltip>
                               <Typography variant="caption" fontWeight="medium">
                                 {activityItem.author?.firstName} {activityItem.author?.lastName}
                                </Typography>
                               <Chip 
                                 label="Activity" 
                                 size="small" 
                                 color="info" 
                                 variant="outlined"
                                 sx={{ fontSize: '0.7rem', height: 20 }}
                               />
                                <Typography variant="caption" color="text.secondary">
                                 {new Date(activityItem.createdAt).toLocaleString()}
                                </Typography>
                              </Box>
                             <Typography variant="body2">{activityItem.content}</Typography>
                            </Box>
                         ))}
                         {activity.length === 0 && (
                           <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                             No activity yet. Changes will appear here.
                           </Typography>
                      )}
                  </Box>
                     )}
                   </Box>
                  </Box>
                )}
             </Paper>
              </Box>

          {/* Right Sidebar */}
          <Box sx={{ 
            width: 350, 
            bgcolor: '#ffffff', 
            borderLeft: '1px solid #e2e8f0',
            p: 3,
            overflow: 'auto',
            maxHeight: '70vh'
          }}>
            {/* Story Details */}
            <Paper sx={{ 
              p: 2.5, 
              mb: 3, 
              borderRadius: 2,
              border: '1px solid #e2e8f0',
              bgcolor: '#f8fafc'
            }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
                Story Details
                </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Type
                  </Typography>
                  <FormControl fullWidth size="small" disabled={!isEditing}>
                  <Select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as IssueType }))}
                    >
                      <MenuItem value={IssueType.STORY}>Story</MenuItem>
                      <MenuItem value={IssueType.BUG}>Bug</MenuItem>
                      <MenuItem value={IssueType.TASK}>Task</MenuItem>
                      <MenuItem value={IssueType.SUBTASK}>Subtask</MenuItem>
                      <MenuItem value={IssueType.EPIC}>Epic</MenuItem>
                      <MenuItem value={IssueType.IMPROVEMENT}>Improvement</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Priority
                  </Typography>
                  <FormControl fullWidth size="small" disabled={!isEditing}>
                    <Select
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as Priority }))}
                    >
                      <MenuItem value={Priority.LOW}>Low</MenuItem>
                      <MenuItem value={Priority.MEDIUM}>Medium</MenuItem>
                      <MenuItem value={Priority.HIGH}>High</MenuItem>
                      <MenuItem value={Priority.CRITICAL}>Critical</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Status
                  </Typography>
                  <FormControl fullWidth size="small" disabled={!isEditing}>
                    <Select
                      value={formData.status}
                      onChange={(e) => {
                        const selectedColumn = boardColumns.find(col => col.status === e.target.value);
                        setFormData(prev => ({ 
                          ...prev, 
                          status: e.target.value as IssueStatus,
                          columnId: selectedColumn?.id || ''
                        }));
                      }}
                    >
                      {boardColumns.map(column => (
                        <MenuItem key={column.id} value={column.status}>
                          {column.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Parent Issue - Only show for subtasks and bugs */}
                {(issue.type === IssueType.SUBTASK || issue.type === IssueType.BUG) && (issue.parent || hydratedIssue?.parent) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Parent Issue
                    </Typography>
                    <Box 
                      sx={{ 
                        p: 1.5, 
                        bgcolor: 'background.paper', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'action.hover',
                          borderColor: 'primary.main',
                          transform: 'translateY(-1px)',
                          boxShadow: 1
                        }
                      }}
                      onClick={() => {
                        const parentIssue = issue.parent || hydratedIssue?.parent;
                        if (parentIssue && onOpenIssue) {
                          handleClose();
                          onOpenIssue(parentIssue);
                        }
                      }}
                    >
                              <Chip
                        icon={getIssueTypeIcon((issue.parent || hydratedIssue?.parent)?.type || IssueType.STORY)}
                        label={(issue.parent || hydratedIssue?.parent)?.type || 'Story'}
                        color={getIssueTypeColor((issue.parent || hydratedIssue?.parent)?.type || IssueType.STORY) as any}
                                size="small"
                        sx={{ fontSize: '0.7rem' }}
                      />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                          {(issue.parent || hydratedIssue?.parent)?.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(issue.parent || hydratedIssue?.parent)?.issueKey}
                        </Typography>
                        </Box>
                      <Box sx={{ 
                        ml: 1,
                        color: 'primary.main',
                        '&:hover': {
                          color: 'primary.dark'
                        }
                      }}>
                        <VisibilityIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </Box>
                  </Box>
                )}

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Story Points
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={formData.storyPoints}
                    onChange={(e) => setFormData(prev => ({ ...prev, storyPoints: parseInt(e.target.value) || 0 }))}
                    onFocus={() => !isEditing && startEditingTransition(() => setIsEditing(true))}
                    InputProps={{ readOnly: !isEditing }}
                    variant="outlined"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Due Date
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    required
                    value={formData.dueDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                    error={!formData.dueDate}
                    helperText={!formData.dueDate ? 'Due date is required' : ''}
                    onFocus={() => !isEditing && startEditingTransition(() => setIsEditing(true))}
                    InputProps={{ readOnly: !isEditing }}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </Box>
              </Box>
             </Paper>

             {/* Time Tracking */}
             <Paper sx={{ 
              p: 2.5, 
              mb: 3, 
              borderRadius: 2,
              border: '1px solid #e2e8f0',
              bgcolor: '#f8fafc'
            }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
                Time Tracking
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Original Estimate (hours)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={formData.originalEstimate}
                    onChange={(e) => setFormData(prev => ({ ...prev, originalEstimate: parseFloat(e.target.value) || 0 }))}
                    onFocus={() => !isEditing && startEditingTransition(() => setIsEditing(true))}
                    InputProps={{ readOnly: !isEditing }}
                    variant="outlined"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Remaining Estimate (hours)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={formData.remainingEstimate}
                    onChange={(e) => setFormData(prev => ({ ...prev, remainingEstimate: parseFloat(e.target.value) || 0 }))}
                    onFocus={() => !isEditing && startEditingTransition(() => setIsEditing(true))}
                    InputProps={{ readOnly: !isEditing }}
                    variant="outlined"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Time Spent (hours)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={issue.timeSpent || 0}
                    disabled
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Paper>

            {/* Labels */}
            <Paper sx={{ 
              p: 2.5, 
              mb: 3, 
              borderRadius: 2,
              border: '1px solid #e2e8f0',
              bgcolor: '#f8fafc'
            }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
                Labels
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Labels (comma-separated)"
                value={formData.labels.join(', ')}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  labels: e.target.value.split(',').map(label => label.trim()).filter(label => label)
                }))}
                onFocus={() => !isEditing && startEditingTransition(() => setIsEditing(true))}
                InputProps={{ readOnly: !isEditing }}
                variant="outlined"
                placeholder="bug, frontend, high-priority"
              />
            </Paper>

            {/* Team Assignment */}
            <Paper sx={{ 
              p: 2.5, 
              mb: 3, 
              borderRadius: 2,
              border: '1px solid #e2e8f0',
              bgcolor: '#f8fafc'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Team Assignment
                </Typography>
                {!isEditing && (
                  <Button
                    size="small"
                    startIcon={<AssignmentIcon />}
                    onClick={() => startEditingTransition(() => setIsEditing(true))}
                    sx={{ textTransform: 'none' }}
                  >
                    Edit
                  </Button>
                )}
              </Box>
              
              {isEditing ? (
                <Box>
                  {/* Search and Filter */}
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search team members..."
                    value={userSearchTerm}
                    sx={{ mb: 2 }}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    variant="outlined"
                  />
                  
                  {/* Available Users Grid */}
                  <Box sx={{ 
                    maxHeight: 180, 
                    overflowY: 'auto', 
                    overflowX: 'hidden',
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 1,
                    p: 1
                  }}>
                    {usersLoaded ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {availableUsers
                          .filter(user => {
                            if (!userSearchTerm) return true;
                            const searchLower = userSearchTerm.toLowerCase();
                            return (
                              user.firstName.toLowerCase().includes(searchLower) ||
                              user.lastName.toLowerCase().includes(searchLower) ||
                              user.email.toLowerCase().includes(searchLower)
                            );
                          })
                          .map((user) => {
                          const isAssigned = formData.assigneeIds.includes(user.id);
                          return (
                            <Box
                              key={user.id}
                              onClick={() => {
                                if (isAssigned) {
                                  setFormData(prev => ({
                                    ...prev,
                                    assigneeIds: prev.assigneeIds.filter(id => id !== user.id)
                                  }));
                                } else {
                                  setFormData(prev => ({
                                    ...prev,
                                    assigneeIds: [...prev.assigneeIds, user.id]
                                  }));
                                }
                              }}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 1.5,
                                borderRadius: 1,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                bgcolor: isAssigned ? 'primary.light' : 'transparent',
                                color: isAssigned ? 'primary.contrastText' : 'text.primary',
                                '&:hover': {
                                  bgcolor: isAssigned ? 'primary.main' : 'action.hover',
                                  transform: 'translateY(-1px)',
                                  boxShadow: 1
                                }
                              }}
                            >
                               <Tooltip title={`${user.firstName} ${user.lastName}`} arrow>
                                 <Avatar 
                                   sx={{ 
                                     width: 28, 
                                     height: 28,
                                     bgcolor: isAssigned ? 'primary.dark' : 'primary.main',
                                     fontSize: '0.75rem'
                                   }}
                                 >
                              {user.firstName?.[0]}{user.lastName?.[0]}
                            </Avatar>
                               </Tooltip>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography 
                                  variant="body2" 
                                  fontWeight="medium"
                                  sx={{ 
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                {user.firstName} {user.lastName}
                              </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    opacity: 0.8,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block'
                                  }}
                                >
                                {user.email}
                              </Typography>
                            </Box>
                              <Box sx={{ 
                                width: 18, 
                                height: 18, 
                                borderRadius: '50%', 
                                border: '2px solid',
                                borderColor: isAssigned ? 'primary.contrastText' : 'divider',
                                bgcolor: isAssigned ? 'primary.contrastText' : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {isAssigned && (
                                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                    ✓
                                  </Typography>
                                )}
                          </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" sx={{ ml: 2 }}>
                          Loading team members...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Quick Actions */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={() => setFormData(prev => ({ ...prev, assigneeIds: [] }))}
                        disabled={formData.assigneeIds.length === 0}
                        sx={{ textTransform: 'none' }}
                      >
                        Clear All
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          if (!usersLoaded) loadAvailableUsers();
                          setFormData(prev => ({ 
                            ...prev, 
                            assigneeIds: availableUsers.map(u => u.id) 
                          }));
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        Assign All
                      </Button>
                    </Box>
                    {formData.assigneeIds.length > 0 && (
                      <Chip 
                        label={`${formData.assigneeIds.length} assigned`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                )}
              </Box>
                </Box>
              ) : (
                /* Read-only view with better styling */
                <Box>
                  {(!issue.assignments || issue.assignments.length === 0) ? (
                    <Box sx={{ 
                      p: 2, 
                      textAlign: 'center', 
                      bgcolor: 'action.hover', 
                      borderRadius: 1,
                      border: '2px dashed',
                      borderColor: 'divider'
                    }}>
                      <PersonIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No team members assigned
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click "Edit" to assign team members
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {issue.assignments.map((assignment) => (
                        <Box 
                          key={assignment.id} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1.5, 
                            p: 1.5, 
                            bgcolor: 'background.paper',
                            borderRadius: 1, 
                            border: '1px solid',
                            borderColor: 'divider',
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: 1,
                              borderColor: 'primary.main'
                            }
                          }}
                        >
                           <Tooltip title={`${assignment.user?.firstName} ${assignment.user?.lastName}`} arrow>
                             <Avatar 
                               sx={{ 
                                 width: 28, 
                                 height: 28,
                                 bgcolor: 'primary.main',
                                 fontSize: '0.75rem'
                               }}
                             >
                               {assignment.user?.firstName?.[0]}{assignment.user?.lastName?.[0]}
                             </Avatar>
                           </Tooltip>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography 
                              variant="body2" 
                              fontWeight="medium"
                              sx={{ 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {assignment.user?.firstName} {assignment.user?.lastName}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block'
                              }}
                            >
                              {assignment.user?.email}
                            </Typography>
                          </Box>
                          <Chip 
                            label={assignment.role || 'Developer'} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ 
                              fontSize: '0.7rem',
                              height: 20,
                              '& .MuiChip-label': {
                                px: 0.5
                              }
                            }}
                          />
                          {isEditing && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                const userId = assignment.user?.id;
                                if (userId) {
                                  setFormData(prev => ({
                                    ...prev,
                                    assigneeIds: prev.assigneeIds.filter(id => id !== userId)
                                  }));
                                }
                              }}
                              sx={{ 
                                ml: 0.5,
                                color: 'error.main',
                                '&:hover': {
                                  bgcolor: 'error.light',
                                  color: 'error.dark'
                                }
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Paper>

             {/* Attachments Section */}
             <Paper sx={{ 
               p: 2.5, 
               mb: 3, 
               borderRadius: 2,
               border: '1px solid #e2e8f0',
               bgcolor: '#f8fafc'
             }}>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
            Attachments
          </Typography>
          {isEditing && (
            <Box>
              <input
                accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                onChange={handleFileUpload}
                disabled={uploadingAttachment}
              />
              <label htmlFor="file-upload">
                <Button
                  component="span"
                         startIcon={uploadingAttachment ? <CircularProgress size={16} /> : <AttachmentIcon />}
                  variant="outlined"
                  size="small"
                  disabled={uploadingAttachment}
                         sx={{ textTransform: 'none' }}
                >
                  {uploadingAttachment ? 'Uploading...' : 'Add Attachment'}
                </Button>
              </label>
            </Box>
          )}
        </Box>
               
        {attachmentsLoading ? (
                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
                   <CircularProgress size={20} />
                   <Typography variant="body2" sx={{ ml: 2 }}>
                     Loading attachments...
            </Typography>
          </Box>
        ) : attachments.length === 0 ? (
                 <Box sx={{ 
                   p: 2, 
                   textAlign: 'center', 
                   bgcolor: 'action.hover', 
                   borderRadius: 1,
                   border: '2px dashed',
                   borderColor: 'divider'
                 }}>
                   <AttachmentIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
                     No attachments yet
            </Typography>
                   {isEditing && (
                     <Typography variant="caption" color="text.secondary">
                       Click "Add Attachment" to upload files
                     </Typography>
                   )}
          </Box>
        ) : (
                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {attachments.map((attachment) => (
                     <Paper key={attachment.id} sx={{ 
                       p: 2, 
                       border: '1px solid #e2e8f0',
                       borderRadius: 2,
                       bgcolor: 'background.paper',
                       transition: 'all 0.2s',
                       '&:hover': {
                         boxShadow: 1,
                         borderColor: 'primary.main'
                       }
                     }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                    {attachment.isImage && attachment.thumbnailUrl ? (
                      <Box
                        component="img"
                        src={getAttachmentUrl({ ...attachment, url: attachment.thumbnailUrl })}
                        alt={attachment.originalName}
                        sx={{
                                 width: 50,
                                 height: 50,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: '1px solid',
                                 borderColor: 'divider',
                                 flexShrink: 0
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                                 width: 50,
                                 height: 50,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          border: '1px solid',
                                 borderColor: 'divider',
                                 flexShrink: 0
                        }}
                      >
                        {getAttachmentIcon(attachment.mimeType)}
                      </Box>
                    )}
                           <Box sx={{ flex: 1, minWidth: 0 }}>
                             <Typography 
                               variant="body2" 
                               fontWeight="medium"
                               sx={{ 
                                 overflow: 'hidden',
                                 textOverflow: 'ellipsis',
                                 whiteSpace: 'nowrap'
                               }}
                             >
                        {attachment.originalName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {attachment.sizeFormatted} • {attachment.mimeType}
                      </Typography>
                             <Typography 
                               variant="caption" 
                               color="text.secondary" 
                               sx={{ 
                                 display: 'block',
                                 overflow: 'hidden',
                                 textOverflow: 'ellipsis',
                                 whiteSpace: 'nowrap'
                               }}
                             >
                               Uploaded by {attachment.uploadedBy?.firstName} {attachment.uploadedBy?.lastName}
                             </Typography>
                             <Typography variant="caption" color="text.secondary">
                               {new Date(attachment.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                         <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title="View attachment">
                      <IconButton
                        size="small"
                        onClick={() => handleViewAttachment(attachment)}
                        color="primary"
                               sx={{ 
                                 '&:hover': {
                                   bgcolor: 'primary.light',
                                   color: 'primary.dark'
                                 }
                               }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download attachment">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadAttachment(attachment)}
                        color="secondary"
                               sx={{ 
                                 '&:hover': {
                                   bgcolor: 'secondary.light',
                                   color: 'secondary.dark'
                                 }
                               }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    {isEditing && (
                      <Tooltip title="Delete attachment">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteAttachment(attachment.id)}
                          color="error"
                                 sx={{ 
                                   '&:hover': {
                                     bgcolor: 'error.light',
                                     color: 'error.dark'
                                   }
                                 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
            </Paper>
              </Box>
              </Box>
            </Box>
            )}

            {/* Sub Tasks Tab (Tab 1) - Only for Story */}
            {issue.type === IssueType.STORY && mainTab === 1 && (
              <Box sx={{ bgcolor: 'white', p: 3 }}>
                {loadingRelatedIssues ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Sub Tasks ({relatedSubtasks.length})
                    </Typography>
                    {relatedSubtasks.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {relatedSubtasks.map((subtask) => {
                          const subtaskBugs = subtaskBugsMap[subtask.id] || [];
                          return (
                            <Paper
                              key={subtask.id}
                              sx={{
                                p: 2,
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: '#f1f5f9',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }
                              }}
                              onClick={() => onOpenIssue?.(subtask)}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Chip
                                  icon={<TaskIcon />}
                                  label={subtask.type}
                                  size="small"
                                  color="primary"
                                />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {subtask.issueKey || `#${subtask.issueNumber}`}
                                </Typography>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {subtask.title}
                                </Typography>
                                <Chip
                                  label={subtask.status}
                                  size="small"
                                  color={subtask.status === 'done' ? 'success' : 'default'}
                                />
                                {subtaskBugs.length > 0 && (
                                  <Tooltip 
                                    title={
                                      <Box>
                                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                                          Bugs: {subtaskBugs.length}
                                        </Typography>
                                        {subtaskBugs.map(bug => (
                                          <Typography key={bug.id} variant="caption" sx={{ display: 'block' }}>
                                            {bug.issueKey || `#${bug.issueNumber}`} - {bug.status}
                                          </Typography>
                                        ))}
                                      </Box>
                                    }
                                  >
                                    <Chip
                                      icon={<BugIcon />}
                                      label={`${subtaskBugs.length} bug${subtaskBugs.length > 1 ? 's' : ''}`}
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No sub tasks found
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Bugs Tab - Tab 2 for Story, Tab 1 for Subtask/Task */}
            {((issue.type === IssueType.STORY && mainTab === 2) || 
             ((issue.type === IssueType.SUBTASK || issue.type === IssueType.TASK) && mainTab === 1)) && (
              <Box sx={{ bgcolor: 'white', p: 3 }}>
                {loadingRelatedIssues ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Bugs ({relatedBugs.length})
                    </Typography>
                    {relatedBugs && relatedBugs.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {relatedBugs.map((bug) => (
                          <Paper
                            key={bug.id}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: '#f1f5f9',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }
                            }}
                            onClick={() => onOpenIssue?.(bug)}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Chip
                                icon={<BugIcon />}
                                label={bug.type}
                                size="small"
                                color="error"
                              />
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {bug.issueKey || `#${bug.issueNumber}`}
                              </Typography>
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {bug.title}
                              </Typography>
                              <Chip
                                label={bug.status}
                                size="small"
                                color={bug.status === 'done' ? 'success' : 'default'}
                              />
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No bugs found
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* Modern Footer */}
      <DialogActions sx={{ 
        p: 3, 
        bgcolor: '#f8fafc', 
        borderTop: '1px solid #e2e8f0',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isEditing && (
            <>
              <Button
                onClick={() => startEditingTransition(() => setIsEditing(false))}
                variant="outlined"
                sx={{ textTransform: 'none' }}
              >
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              variant="outlined" 
              disabled={loading || isPending}
              startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
              sx={{ 
                textTransform: 'none',
                borderColor: '#64748b',
                color: '#334155',
                '&:hover': {
                  borderColor: '#334155',
                  backgroundColor: 'rgba(51, 65, 85, 0.04)',
                },
                '&:disabled': {
                  borderColor: '#cbd5e1',
                  color: '#94a3b8',
                }
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default IssueDetailsModal; 
